import {
  ZadDeliveryNoteItem,
  ZadLocalizedText,
  ZadOfferDiscountType,
  ZadSaleItem,
  ZadSalePromoCodePreview,
  ZadSalePromoCodeSnapshot,
  ZadSalePromotionSnapshot,
} from '../../../models/zad'
import {formatZadTnd, roundZadMoney3} from './productHelpers'
import {normalizeZadOfferCode} from './promotionHelpers'

export function emptyZadSaleLocalized(): ZadLocalizedText {
  return {fr: '', en: '', ar: ''}
}

function asLocalized(value: unknown): ZadLocalizedText {
  if (typeof value === 'string') return {fr: value.trim(), en: '', ar: ''}
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  return {
    fr: String(raw.fr || '').trim(),
    en: String(raw.en || '').trim(),
    ar: String(raw.ar || '').trim(),
  }
}

function asDiscountType(value: unknown): ZadOfferDiscountType | null {
  if (value === 'PERCENTAGE' || value === 'FIXED_AMOUNT') return value
  return null
}

export function sanitizeZadSalePromotionSnapshot(value: unknown): ZadSalePromotionSnapshot | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const discountType = asDiscountType(raw.discountType)
  const promotionalUnitPrice = Number(raw.promotionalUnitPrice)
  const discountValue = Number(raw.discountValue)
  const originalUnitPrice = Number(raw.originalUnitPrice)
  const unitDiscountAmount = Number(raw.unitDiscountAmount)
  if (!discountType || !Number.isFinite(promotionalUnitPrice) || !Number.isFinite(discountValue)) {
    return null
  }
  return {
    promotionId: String(raw.promotionId || ''),
    title: asLocalized(raw.title),
    discountType,
    discountValue,
    originalUnitPrice: Number.isFinite(originalUnitPrice) ? originalUnitPrice : 0,
    promotionalUnitPrice,
    unitDiscountAmount: Number.isFinite(unitDiscountAmount) ? unitDiscountAmount : 0,
  }
}

export function pickZadSaleLocalized(value: ZadLocalizedText | undefined, locale: string): string {
  const text = value || emptyZadSaleLocalized()
  const lang = String(locale || 'fr').slice(0, 2)
  return text[lang as keyof ZadLocalizedText] || text.fr || text.en || text.ar || ''
}

export function formatZadSalePromotionLabel(snapshot: Pick<ZadSalePromotionSnapshot, 'discountType' | 'discountValue'>): string {
  if (snapshot.discountType === 'PERCENTAGE') return `-${snapshot.discountValue}%`
  return `-${formatZadTnd(snapshot.discountValue)}`
}

export function getZadSaleItemDisplayUnitPrice(
  item: Pick<ZadSaleItem, 'unitPrice' | 'promotionSnapshot'>
): number {
  const snapshot = sanitizeZadSalePromotionSnapshot(item.promotionSnapshot)
  if (snapshot) return snapshot.promotionalUnitPrice
  return Number(item.unitPrice) || 0
}

export function buildZadSaleTicketItemView(
  item: Pick<
    ZadSaleItem | ZadDeliveryNoteItem,
    | 'productName'
    | 'productCode'
    | 'quantity'
    | 'unitPrice'
    | 'discountType'
    | 'discountValue'
    | 'lineTotal'
    | 'promotionSnapshot'
  > & {
    saleUnit?: string | null
    packagingLabelSnapshot?: string | null
  }
) {
  const snapshot = sanitizeZadSalePromotionSnapshot(item.promotionSnapshot)
  const baseName = String(item.productName || item.productCode || '').trim() || '—'
  const packLabel = String((item as ZadSaleItem).packagingLabelSnapshot || '').trim()
  const saleUnit = String((item as ZadSaleItem).saleUnit || '').toUpperCase()
  const name =
    packLabel && packLabel !== 'UNIT'
      ? `${baseName} — ${packLabel}`
      : saleUnit === 'PACKAGE'
        ? `${baseName} — PACKAGE`
        : saleUnit === 'UNIT' && packLabel
          ? `${baseName} — Unité`
          : baseName
  return {
    name,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    displayUnitPrice: snapshot ? snapshot.promotionalUnitPrice : item.unitPrice,
    hasPromotion: Boolean(snapshot),
    originalUnitPrice: snapshot ? snapshot.originalUnitPrice : item.unitPrice,
    promotionalUnitPrice: snapshot ? snapshot.promotionalUnitPrice : item.unitPrice,
    promotionLabel: snapshot ? formatZadSalePromotionLabel(snapshot) : '',
    promotionTitle: snapshot?.title || emptyZadSaleLocalized(),
    lineTotal: item.lineTotal,
    hasManualDiscount: Boolean(item.discountType && item.discountType !== 'NONE' && Number(item.discountValue) > 0),
  }
}

export function normalizeZadSalePromoCodeInput(value: unknown): string {
  return normalizeZadOfferCode(value)
}

export function sanitizeZadSalePromoCodeSnapshot(value: unknown): ZadSalePromoCodeSnapshot | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const code = normalizeZadSalePromoCodeInput(raw.code)
  const discountType = asDiscountType(raw.discountType)
  const discountValue = Number(raw.discountValue)
  const discountAmount = Number(raw.discountAmount)
  const eligibleAmount = Number(raw.eligibleAmount ?? raw.eligibleAmountBeforeCode)
  const saleTotalAfterCode = Number(
    raw.saleTotalAfterCode ?? raw.totalAfterPromoCode ?? raw.saleTotal
  )
  if (!code || !discountType || !Number.isFinite(discountValue) || !Number.isFinite(discountAmount)) {
    return null
  }
  return {
    code,
    title: asLocalized(raw.title),
    discountType,
    discountValue,
    eligibleAmount: Number.isFinite(eligibleAmount) ? eligibleAmount : 0,
    discountAmount,
    saleTotalAfterCode: Number.isFinite(saleTotalAfterCode) ? saleTotalAfterCode : 0,
  }
}

export function sanitizeZadSalePromoCodePreview(value: unknown): ZadSalePromoCodePreview | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const code = normalizeZadSalePromoCodeInput(raw.code)
  const discountType = asDiscountType(raw.discountType)
  const discountValue = Number(raw.discountValue)
  const subtotal = Number(raw.subtotal)
  const automaticPromotionDiscount = Number(raw.automaticPromotionDiscount ?? 0)
  const itemDiscountTotal = Number(raw.itemDiscountTotal)
  const globalDiscountAmount = Number(raw.globalDiscountAmount)
  const eligibleAmount = Number(raw.eligibleAmount)
  const promoCodeDiscountTotal = Number(raw.promoCodeDiscountTotal)
  const expectedTotal = Number(raw.expectedTotal)
  if (
    !code ||
    !discountType ||
    !Number.isFinite(discountValue) ||
    !Number.isFinite(subtotal) ||
    !Number.isFinite(automaticPromotionDiscount) ||
    !Number.isFinite(itemDiscountTotal) ||
    !Number.isFinite(globalDiscountAmount) ||
    !Number.isFinite(eligibleAmount) ||
    !Number.isFinite(promoCodeDiscountTotal) ||
    !Number.isFinite(expectedTotal)
  ) {
    return null
  }
  return {
    code,
    title: asLocalized(raw.title),
    discountType,
    discountValue,
    subtotal,
    automaticPromotionDiscount,
    itemDiscountTotal,
    globalDiscountAmount,
    eligibleAmount,
    promoCodeDiscountTotal,
    expectedTotal,
  }
}

export function extractZadSalePromoCodePreview(payload: unknown): ZadSalePromoCodePreview | null {
  const raw = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {}
  return sanitizeZadSalePromoCodePreview(raw.preview ?? raw)
}

export function toZadSalePromoCodePreviewTotals(preview: ZadSalePromoCodePreview) {
  return {
    subtotal: preview.subtotal,
    promotionDiscountTotal: preview.automaticPromotionDiscount,
    itemDiscountTotal: preview.itemDiscountTotal,
    afterItemDiscounts: roundZadMoney3(
      preview.subtotal - preview.automaticPromotionDiscount - preview.itemDiscountTotal
    ),
    globalDiscountAmount: preview.globalDiscountAmount,
    promoCode: preview.code,
    promoCodeDiscountTotal: preview.promoCodeDiscountTotal,
    totalDiscount: roundZadMoney3(
      preview.automaticPromotionDiscount +
        preview.itemDiscountTotal +
        preview.globalDiscountAmount +
        preview.promoCodeDiscountTotal
    ),
    total: preview.expectedTotal,
  }
}

export function formatZadSalePromoCodeDiscountLabel(
  preview: Pick<ZadSalePromoCodePreview, 'discountType' | 'discountValue'>
): string {
  if (preview.discountType === 'PERCENTAGE') return `-${preview.discountValue} %`
  return `-${formatZadTnd(preview.discountValue)}`
}

export function shouldCommitZadSalePromoCodePreview(params: {
  requestSeq: number
  currentSeq: number
  requestedCode: string
  currentCode: string
}): boolean {
  if (params.requestSeq !== params.currentSeq) return false
  return (
    normalizeZadSalePromoCodeInput(params.requestedCode) ===
    normalizeZadSalePromoCodeInput(params.currentCode)
  )
}
